import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ChevronLeft, ChevronRight, Star, Quote, PenLine } from "lucide-react";
import { api } from "../lib/api";
import { useI18n } from "../contexts/I18nContext";
import PublicReviewModal from "./PublicReviewModal";

export default function Testimonials() {
  const { t, lang } = useI18n();
  const [items, setItems] = useState([]);
  const [stats, setStats] = useState({ count: 0, avg: 0 });
  const [idx, setIdx] = useState(0);
  const [openReview, setOpenReview] = useState(false);

  useEffect(() => {
    api.get("/reviews/site").then((r) => setItems(r.data || [])).catch(() => {});
    api.get("/reviews/stats").then((r) => setStats(r.data || { count: 0, avg: 0 })).catch(() => {});
  }, []);

  // Auto-rotate every 6s
  useEffect(() => {
    if (items.length <= 1) return;
    const id = setInterval(() => setIdx((i) => (i + 1) % items.length), 6000);
    return () => clearInterval(id);
  }, [items.length]);

  const fmtDate = (iso) => {
    if (!iso) return "";
    try {
      const d = new Date(iso);
      return d.toLocaleDateString(lang === "en" ? "en-GB" : "ro-RO", { year: "numeric", month: "long" });
    } catch { return ""; }
  };

  const prev = () => setIdx((i) => (i - 1 + items.length) % items.length);
  const next = () => setIdx((i) => (i + 1) % items.length);

  return (
    <section
      id="testimonials"
      className="py-24 md:py-32 px-6 md:px-12 max-w-[1440px] mx-auto"
      data-testid="testimonials-section"
    >
      <div className="text-center mb-16">
        <div className="label-eyebrow mb-4" style={{ color: "var(--brand-primary)" }}>
          {t("home.testimonialsEyebrow")}
        </div>
        <h2 className="font-serif font-light text-4xl sm:text-5xl tracking-tight mb-6">
          {t("home.testimonialsTitle")}
        </h2>
        {stats.count > 0 && (
          <div className="inline-flex items-center gap-3 px-5 py-2.5 rounded-full border" style={{ borderColor: "var(--brand-border)", background: "var(--brand-surface)" }}>
            <div className="flex items-center gap-0.5">
              {[1, 2, 3, 4, 5].map((s) => (
                <Star
                  key={s}
                  className="w-4 h-4"
                  fill={s <= Math.round(stats.avg) ? "var(--brand-primary)" : "transparent"}
                  stroke="var(--brand-primary)"
                />
              ))}
            </div>
            <span className="text-sm font-medium" style={{ color: "var(--brand-text-primary)" }} data-testid="testimonials-avg">
              {stats.avg.toFixed(1)} / 5
            </span>
            <span className="text-xs" style={{ color: "var(--brand-text-secondary)" }}>
              · {stats.count} {t("home.totalReviews")}
            </span>
          </div>
        )}
      </div>

      {items.length === 0 ? (
        <div
          className="text-center py-16 text-sm card-surface"
          style={{ color: "var(--brand-text-secondary)" }}
          data-testid="testimonials-empty"
        >
          {t("home.noReviewsYet")}
        </div>
      ) : (
        <div className="relative max-w-3xl mx-auto" data-testid="testimonials-carousel">
          <div className="card-surface p-8 sm:p-12 relative">
            <Quote
              className="absolute top-6 left-6 w-10 h-10 opacity-20"
              style={{ color: "var(--brand-primary)" }}
            />
            <div className="text-center" key={items[idx]?.id || idx}>
              <div className="flex items-center justify-center gap-0.5 mb-6">
                {[1, 2, 3, 4, 5].map((s) => (
                  <Star
                    key={s}
                    className="w-4 h-4"
                    fill={s <= (items[idx]?.rating || 0) ? "var(--brand-primary)" : "transparent"}
                    stroke="var(--brand-primary)"
                  />
                ))}
              </div>
              <p
                className="font-serif font-light text-xl sm:text-2xl leading-relaxed mb-8 italic"
                data-testid="testimonial-text"
              >
                "{items[idx]?.comment || items[idx]?.text || ""}"
              </p>
              <div className="text-sm" style={{ color: "var(--brand-primary)" }}>
                {items[idx]?.guest_name || items[idx]?.author || "Anonim"}
              </div>
              <div className="text-xs mt-1" style={{ color: "var(--brand-text-secondary)" }}>
                {fmtDate(items[idx]?.created_at)}
              </div>
            </div>
          </div>

          {items.length > 1 && (
            <>
              <button
                onClick={prev}
                aria-label="previous"
                className="absolute -left-2 sm:-left-12 top-1/2 -translate-y-1/2 w-10 h-10 flex items-center justify-center rounded-full border transition hover:bg-white/5"
                style={{ borderColor: "var(--brand-border)", background: "var(--brand-bg)", color: "var(--brand-primary)" }}
                data-testid="testimonials-prev"
              >
                <ChevronLeft className="w-4 h-4" />
              </button>
              <button
                onClick={next}
                aria-label="next"
                className="absolute -right-2 sm:-right-12 top-1/2 -translate-y-1/2 w-10 h-10 flex items-center justify-center rounded-full border transition hover:bg-white/5"
                style={{ borderColor: "var(--brand-border)", background: "var(--brand-bg)", color: "var(--brand-primary)" }}
                data-testid="testimonials-next"
              >
                <ChevronRight className="w-4 h-4" />
              </button>
              <div className="flex justify-center gap-1.5 mt-6">
                {items.map((_, i) => (
                  <button
                    key={i}
                    onClick={() => setIdx(i)}
                    aria-label={`go to ${i + 1}`}
                    className="h-1.5 rounded-full transition-all"
                    style={{
                      width: i === idx ? "24px" : "8px",
                      background: i === idx ? "var(--brand-primary)" : "var(--brand-border)",
                    }}
                    data-testid={`testimonials-dot-${i}`}
                  />
                ))}
              </div>
            </>
          )}
        </div>
      )}

      <div className="text-center mt-12 flex flex-wrap justify-center gap-3">
        <button
          onClick={() => setOpenReview(true)}
          className="btn-gold-filled inline-flex items-center gap-2"
          data-testid="testimonials-write-review"
        >
          <PenLine className="w-4 h-4" />
          {lang === "en" ? "Write a review" : "Scrie o recenzie"}
        </button>
        <Link
          to="/reviews"
          className="btn-ghost inline-flex items-center gap-2"
          data-testid="testimonials-view-all"
        >
          {t("home.viewAllReviews")}
          <ChevronRight className="w-4 h-4" />
        </Link>
      </div>

      <PublicReviewModal open={openReview} onClose={() => setOpenReview(false)} targetType="site" />
    </section>
  );
}
